import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  FileText, Download, Upload, Search, RefreshCw, 
  Shield, Lock, Calendar, DollarSign, AlertTriangle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const YEARS = [2026, 2025, 2024, 2023];

export default function Payroll() {
  const { api, isAdmin, user } = useAuth();
  const [paystubs, setPaystubs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('paystubs');

  // Upload dialog (admin only)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadData, setUploadData] = useState({
    employee_id: '',
    doc_type: 't4',
    pay_period: '',
    description: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch paystubs - works for both admin and employees
      const paystubsRes = await api.get('/payroll/paystubs');
      setPaystubs(paystubsRes.data);

      if (isAdmin) {
        // Admin also loads employees and any uploaded documents
        const [usersRes, docsRes] = await Promise.all([
          api.get('/users'),
          api.get('/documents/payroll').catch(() => ({ data: [] })) // May not exist yet
        ]);
        setEmployees(usersRes.data.filter(u => u.role === 'employee'));
        setDocuments(docsRes.data || []);
      }
    } catch (error) {
      console.error('Payroll fetch error:', error);
      let errorMsg = 'Failed to fetch payroll documents';
      if (error.response?.status === 403) {
        if (error.response?.data?.detail?.includes('physician')) {
          errorMsg = 'Payroll documents are only available for employees';
        } else {
          errorMsg = 'Access denied';
        }
      } else if (error.response?.status === 401) {
        errorMsg = 'Session expired. Please log in again.';
      } else if (error.message === 'Network Error') {
        errorMsg = 'Unable to reach server. Check your connection.';
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPaystub = async (paystubId) => {
    try {
      const response = await api.get(`/payroll/paystubs/${paystubId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paystub_${paystubId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      let errorMsg = 'Failed to download paystub';
      if (error.response?.status === 404) {
        errorMsg = 'Paystub PDF not found';
      } else if (error.response?.status === 403) {
        errorMsg = 'You do not have permission to download this paystub';
      }
      toast.error(errorMsg);
    }
  };

  const handleDownloadDocument = async (docId) => {
    try {
      const response = await api.get(`/documents/payroll/${docId}/download`, {
        responseType: 'blob'
      });
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `document_${docId}.pdf`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    if (!uploadData.employee_id) {
      toast.error('Please select an employee');
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const params = new URLSearchParams({
        user_id: uploadData.employee_id,
        doc_type: uploadData.doc_type,
        pay_period: uploadData.pay_period || `${new Date().getFullYear()}`
      });

      await api.post(`/documents/payroll?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadData({ employee_id: '', doc_type: 't4', pay_period: '', description: '' });
      setSelectedFile(null);
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      finalized: 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  // Employee view - simplified
  if (!isAdmin) {
    const finalizedPaystubs = paystubs.filter(p => p.status === 'finalized');
    
    return (
      <div className="space-y-6" data-testid="payroll-employee-view">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">My Pay Documents</h1>
          <p className="text-slate-500 mt-1">View your paystubs and tax documents</p>
        </div>

        <Alert className="bg-blue-50 border-blue-200">
          <Shield className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-700">
            Your payroll documents are private and secure. Only you and authorized administrators can access them.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />
              My Paystubs
            </CardTitle>
            <CardDescription>
              View and download your pay statements
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : finalizedPaystubs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-slate-300" />
                <p className="mt-4 text-slate-500">No paystubs available yet</p>
                <p className="text-sm text-slate-400 mt-2">
                  Paystubs will appear here after payroll processing
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pay Period</TableHead>
                    <TableHead>Pay Date</TableHead>
                    <TableHead>Gross Pay</TableHead>
                    <TableHead>Deductions</TableHead>
                    <TableHead>Net Pay</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {finalizedPaystubs.map((stub) => (
                    <TableRow key={stub.id} data-testid={`paystub-row-${stub.id}`}>
                      <TableCell>
                        <div className="font-medium">
                          {stub.pay_period_start} to {stub.pay_period_end}
                        </div>
                      </TableCell>
                      <TableCell>{stub.pay_date}</TableCell>
                      <TableCell>${stub.gross_earnings?.toFixed(2)}</TableCell>
                      <TableCell className="text-red-600">
                        -${stub.total_deductions?.toFixed(2)}
                      </TableCell>
                      <TableCell className="font-bold text-emerald-600">
                        ${stub.net_pay?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownloadPaystub(stub.id)}
                          data-testid={`download-btn-${stub.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin view - full functionality
  return (
    <div className="space-y-6" data-testid="payroll-admin-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Payroll Documents</h1>
          <p className="text-slate-500 mt-1">Manage employee pay documents and tax forms</p>
        </div>
        <Button 
          onClick={() => setUploadDialogOpen(true)}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="upload-doc-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="paystubs" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Paystubs ({paystubs.length})
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Tax Documents ({documents.length})
          </TabsTrigger>
        </TabsList>

        {/* Paystubs Tab */}
        <TabsContent value="paystubs">
          <Card>
            <CardHeader>
              <CardTitle>Generated Paystubs</CardTitle>
              <CardDescription>
                Paystubs generated through the payroll calculator
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : paystubs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-slate-300" />
                  <p className="mt-4 text-slate-500">No paystubs generated yet</p>
                  <p className="text-sm text-slate-400 mt-2">
                    Use the Payroll Calculator to generate paystubs
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Pay Period</TableHead>
                      <TableHead>Pay Date</TableHead>
                      <TableHead>Gross</TableHead>
                      <TableHead>Net Pay</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paystubs.map((stub) => (
                      <TableRow key={stub.id}>
                        <TableCell className="font-medium">{stub.employee_name}</TableCell>
                        <TableCell className="text-sm">
                          {stub.pay_period_start} to {stub.pay_period_end}
                        </TableCell>
                        <TableCell>{stub.pay_date}</TableCell>
                        <TableCell>${stub.gross_earnings?.toFixed(2)}</TableCell>
                        <TableCell className="font-bold text-emerald-600">
                          ${stub.net_pay?.toFixed(2)}
                        </TableCell>
                        <TableCell>{getStatusBadge(stub.status)}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownloadPaystub(stub.id)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Tax Documents</CardTitle>
              <CardDescription>
                T4s and other tax documents uploaded for employees
              </CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-slate-300" />
                  <p className="mt-4 text-slate-500">No tax documents uploaded yet</p>
                  <p className="text-sm text-slate-400 mt-2">
                    Click "Upload Document" to add T4s or other tax forms
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Document Type</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Filename</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">{doc.user_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{doc.doc_type?.toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell>{doc.pay_period}</TableCell>
                        <TableCell className="text-sm">{doc.filename}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {doc.created_at ? format(parseISO(doc.created_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownloadDocument(doc.id)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Payroll Document</DialogTitle>
            <DialogDescription>
              Upload T4s or other tax documents for employees
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select 
                  value={uploadData.employee_id} 
                  onValueChange={(v) => setUploadData({ ...uploadData, employee_id: v })}
                >
                  <SelectTrigger data-testid="upload-employee">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Document Type *</Label>
                  <Select 
                    value={uploadData.doc_type} 
                    onValueChange={(v) => setUploadData({ ...uploadData, doc_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="t4">T4 - Employment Income</SelectItem>
                      <SelectItem value="t4a">T4A - Other Income</SelectItem>
                      <SelectItem value="rl1">RL-1 (Quebec)</SelectItem>
                      <SelectItem value="other">Other Tax Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tax Year / Period</Label>
                  <Input
                    value={uploadData.pay_period}
                    onChange={(e) => setUploadData({ ...uploadData, pay_period: e.target.value })}
                    placeholder="e.g., 2025"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>File *</Label>
                <Input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  data-testid="upload-file"
                />
                <p className="text-xs text-slate-500">Accepted: PDF, PNG, JPG (max 10MB)</p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={uploadLoading}
              >
                {uploadLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
